/**
 * Attributed orders and the commission they generated.
 *
 * Read-only by design: orders arrive from a store's ingestion call, and the
 * conversions and ledger entries are computed from them at that moment.
 * Editing an order here would decouple it from money already accounted for.
 *
 * Commission status is shown per conversion rather than per order, because a
 * basket can mix a 20%-commission item with a 0% one, and an order-level
 * figure would be wrong for both. That detail lives behind a row expander so
 * the list stays readable when nobody is asking the question.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SectionCard from '../../_helpers/reusableComponents/design/SectionCard';
import EmptyState from '../../_helpers/reusableComponents/design/EmptyState';
import StatusPill from '../../_helpers/reusableComponents/design/StatusPill';
import {
  palette,
  radius,
  motion,
  typography,
} from '../../_helpers/reusableComponents/design/designTokens';
import CommerceService, { readApiError } from '../../_services/commerce/CommerceService';

// Commission statuses spoken in the design system's status vocabulary, so a
// sale here is coloured the same way every other state in the product is.
// PENDING/ENDED/PUBLISHED/CANCELLED are the token keys, not the words shown —
// the label carries the wording that is actually read.
const STATUS_TOKEN = {
  PENDING: 'PENDING',
  APPROVED: 'ENDED',
  PAID: 'PUBLISHED',
  REVERSED: 'CANCELLED',
  PARTIALLY_REVERSED: 'CANCELLED',
  REJECTED: 'DRAFT',
};

const STATUS_HELP = {
  PENDING: 'Still inside the return window — can still be reversed',
  APPROVED: 'Confirmed and owed to the creator',
  PAID: 'Settled',
  REVERSED: 'The order was returned, so this was clawed back',
  PARTIALLY_REVERSED: 'Part of the order was returned',
  REJECTED: 'Not eligible for commission',
};

const FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: `${radius.sm}px`,
    bgcolor: palette.surface,
    '& fieldset': { borderColor: palette.line },
    '&:hover fieldset': { borderColor: palette.hint },
    '&.Mui-focused fieldset': { borderColor: palette.brand, borderWidth: 1.5 },
  },
};

const HEAD_ROW_SX = {
  '& th': {
    ...typography.eyebrow,
    bgcolor: palette.surfaceAlt,
    borderBottom: `1px solid ${palette.line}`,
    py: 1.25,
    whiteSpace: 'nowrap',
  },
};

const OrderRow = ({ order }) => {
  const [open, setOpen] = useState(false);
  const hasConversions = (order.conversions || []).length > 0;

  return (
    <>
      <TableRow
        hover
        sx={{
          transition: `background-color ${motion.fast}`,
          '&:hover': { backgroundColor: palette.brandSofter },
          // An expanded order and its detail panel are one block, so the rule
          // between them is dropped while it is open.
          '& td': { borderBottom: open ? 0 : `1px solid ${palette.line}` },
          ...(open && { backgroundColor: palette.brandSofter }),
        }}
      >
        <TableCell width={48}>
          {/* First button in the row, and the only one — the expander. */}
          {hasConversions && (
            <IconButton
              size="small"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? 'Hide commission detail' : 'Show commission detail'}
              sx={{
                color: open ? palette.brand : palette.muted,
                '&:hover': { color: palette.brand, bgcolor: palette.brandSoft },
              }}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Typography sx={{ ...typography.mono, fontSize: 13, fontWeight: 700, color: palette.ink }}>
            {order.order_number}
          </Typography>
          <Typography sx={{ fontSize: 12, color: palette.muted }}>
            {order.buyer_email || 'no buyer email'}
          </Typography>
        </TableCell>
        <TableCell
          align="right"
          sx={{ ...typography.money, fontWeight: 700, color: palette.ink }}
        >
          {`${order.currency} ${order.total_amount}`}
        </TableCell>
        <TableCell>
          <StatusPill status={order.payment_status} />
        </TableCell>
        <TableCell>
          {order.creator_ref ? (
            <Stack spacing={0.25}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: palette.ink }}>
                {order.creator_ref}
              </Typography>
              <Typography sx={{ fontSize: 11, color: palette.muted }}>
                via {order.attribution_source || 'unknown'}
              </Typography>
            </Stack>
          ) : (
            <Typography sx={{ fontSize: 12, color: palette.hint, fontStyle: 'italic' }}>
              Not attributed
            </Typography>
          )}
        </TableCell>
        <TableCell align="right" sx={{ ...typography.money, color: palette.inkSoft }}>
          {hasConversions ? order.conversions.length : '—'}
        </TableCell>
        <TableCell sx={{ fontSize: 12, color: palette.muted, whiteSpace: 'nowrap' }}>
          {order.placed_at ? new Date(order.placed_at).toLocaleString() : '—'}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell
          colSpan={7}
          sx={{
            py: 0,
            borderBottom: open ? `1px solid ${palette.line}` : 'none',
            bgcolor: open ? palette.surfaceAlt : 'transparent',
          }}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2.5, px: { xs: 0, sm: 1 } }}>
              <Typography sx={{ ...typography.eyebrow, mb: 1.25 }}>
                Commission on this order
              </Typography>
              <Box
                sx={{
                  bgcolor: palette.surface,
                  border: `1px solid ${palette.line}`,
                  borderRadius: `${radius.sm}px`,
                  overflow: 'hidden',
                }}
              >
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 720 }}>
                    <TableHead>
                      <TableRow sx={HEAD_ROW_SX}>
                        <TableCell>Item</TableCell>
                        <TableCell>Creator</TableCell>
                        <TableCell>Rule applied</TableCell>
                        <TableCell align="right">Commissionable</TableCell>
                        <TableCell align="right">Commission</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(order.conversions || []).map((conversion) => (
                        <TableRow
                          key={conversion.public_id}
                          sx={{ '&:last-of-type td': { borderBottom: 0 } }}
                        >
                          <TableCell sx={{ fontSize: 13, fontWeight: 600, color: palette.ink }}>
                            {conversion.product_title}
                          </TableCell>
                          <TableCell sx={{ fontSize: 13, color: palette.inkSoft }}>
                            {conversion.external_creator_ref}
                          </TableCell>
                          <TableCell>
                            {/*
                              The rule is snapshotted on the conversion, so this
                              shows what was actually applied at the time -- not
                              what the rule says today.
                            */}
                            <Typography sx={{ fontSize: 13, color: palette.ink }}>
                              {conversion.rule_name_snapshot || '—'}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: palette.muted }}>
                              {conversion.rule_rate_type === 'PERCENT'
                                ? `${conversion.rule_rate_value}% at time of sale`
                                : `${conversion.rule_rate_value} per unit at time of sale`}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ ...typography.money, color: palette.inkSoft }}
                          >
                            {conversion.commissionable_amount}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              sx={{ ...typography.money, fontSize: 13, fontWeight: 700, color: palette.ink }}
                            >
                              {conversion.commission_amount}
                            </Typography>
                            {/* A clawback is stated, not implied by a smaller
                                number — the original is what it is measured
                                against. */}
                            {conversion.commission_amount !==
                              conversion.original_commission_amount && (
                              <Typography
                                sx={{ ...typography.money, fontSize: 11, color: palette.danger }}
                              >
                                was {conversion.original_commission_amount}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Tooltip arrow title={STATUS_HELP[conversion.status] || ''}>
                              <Box component="span" sx={{ display: 'inline-flex' }}>
                                <StatusPill
                                  status={STATUS_TOKEN[conversion.status] || 'DRAFT'}
                                  label={conversion.status?.replaceAll('_', ' ')}
                                  size="sm"
                                />
                              </Box>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const AffiliateOrders = ({ onNotify }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const notify = useCallback(
    (message, severity = 'info') => {
      if (onNotify) onNotify(message, severity);
    },
    [onNotify]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.q = search;
      const { data } = await CommerceService.listOrders(params);
      setOrders(data.results || data);
    } catch (error) {
      notify(readApiError(error, 'Could not load orders.'), 'error');
    } finally {
      setLoading(false);
    }
  }, [search, notify]);

  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  // Loading, empty and populated are three peer states; chaining them as
  // nested ternaries inside the JSX read as a single condition.
  let orderList;
  if (loading) {
    orderList = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: palette.brand }} />
      </Box>
    );
  } else if (orders.length === 0) {
    orderList = (
      <Box sx={{ p: 2.5 }}>
        <EmptyState
          icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 30 }} />}
          title="No orders yet"
          description="Orders show up here as soon as your store reports a sale. Once one is attributed to a creator, you will see what they earned on it."
        />
      </Box>
    );
  } else {
    orderList = (
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow sx={HEAD_ROW_SX}>
              <TableCell />
              <TableCell>Order</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell>Attributed to</TableCell>
              <TableCell align="right">Conversions</TableCell>
              <TableCell>Placed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => (
              <OrderRow key={order.public_id} order={order} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Box>
      <SectionCard
        title="Orders & earnings"
        subtitle="Every sale your store reported, and the commission it generated."
        noPadBody
        actions={
          <TextField
            size="small"
            placeholder="Search order number, buyer or creator"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: palette.hint }} />
                </InputAdornment>
              ),
            }}
            sx={{ ...FIELD_SX, minWidth: { xs: '100%', sm: 300 } }}
          />
        }
      >
        {orderList}
      </SectionCard>
    </Box>
  );
};

export default AffiliateOrders;
